import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for typewriter/typing animation effect
 * Simulates text being typed with natural, graceful timing variations
 * Mimics AI thinking patterns with variable speeds and pauses
 * 
 * @param {string} fullText - The complete text to be typed out
 * @param {Object} options - Configuration options
 * @param {number} options.speed - Base typing speed in milliseconds per character (default: 60)
 * @param {number} options.startDelay - Delay before starting animation in ms (default: 0)
 * @param {boolean} options.enabled - Whether animation is enabled (default: true)
 * @param {boolean} options.wordMode - Type whole words at once instead of characters (default: false)
 * @returns {Object} { displayText, isTyping, isDone }
 */
export const useTypewriter = (fullText, options = {}) => {
  const {
    speed = 60, // Slower base speed for more graceful feel
    startDelay = 0,
    enabled = true,
    wordMode = false
  } = options;

  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [currentlyTypingIndex, setCurrentlyTypingIndex] = useState(-1);
  
  const timeoutRef = useRef(null);
  const currentIndexRef = useRef(0);
  
  useEffect(() => {
    // If animation is disabled, show full text immediately
    if (!enabled || !fullText) {
      setDisplayText(fullText);
      setIsTyping(false);
      setIsDone(true);
      return;
    }

    // Reset state when fullText changes
    setDisplayText('');
    setIsTyping(false);
    setIsDone(false);
    setCurrentlyTypingIndex(-1);
    currentIndexRef.current = 0;

    // Cancel any existing animations
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Deterministic delay per character without additional pauses or jitter
    const getVariableDelay = () => speed;

    const startAnimation = () => {
      setIsTyping(true);
      
      const typeNextCharacter = () => {
        const currentIndex = currentIndexRef.current;
        
        if (currentIndex < fullText.length) {
          let nextIndex;
          
          if (wordMode) {
            // Find the end of the current word
            nextIndex = currentIndex;
            // Skip to end of current word (until space or end of string)
            while (nextIndex < fullText.length && fullText[nextIndex] !== ' ') {
              nextIndex++;
            }
            // Include the space if it exists
            if (nextIndex < fullText.length && fullText[nextIndex] === ' ') {
              nextIndex++;
            }
          } else {
            // Character mode - just advance by one character
            nextIndex = currentIndex + 1;
          }
          
          // Set the currently typing position for fade effect
          setCurrentlyTypingIndex(nextIndex - 1);
          
          setDisplayText(fullText.substring(0, nextIndex));
          currentIndexRef.current = nextIndex;
          
          // Calculate natural delay for next character
          const delay = getVariableDelay();
          
          // Schedule next character with natural timing
          timeoutRef.current = setTimeout(typeNextCharacter, delay);
        } else {
          // Animation complete
          setIsTyping(false);
          setIsDone(true);
          setCurrentlyTypingIndex(-1);
          setDisplayText(fullText);
        }
      };
      
      // Start typing
      typeNextCharacter();
    };

    // Start animation after delay
    if (startDelay > 0) {
      timeoutRef.current = setTimeout(startAnimation, startDelay);
    } else {
      startAnimation();
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [fullText, speed, startDelay, enabled, wordMode]);

  return {
    displayText,
    isTyping,
    isDone,
    currentlyTypingIndex
  };
};

export default useTypewriter;
